import {
  rememberInvestorPricingReturn,
  shouldRememberInvestorPricingReturn,
} from './investorPricingReturnScroll';

const PRAIRI_LINK_HOSTS = new Set(['www.prairi.com', 'prairi.com', 'www.builder.my', 'builder.my']);

/** Builder.io content may use stale paths — map them to current app routes. */
export const BUILDER_LINK_REWRITES: Record<string, string> = {
  '/landing/investor': '/investors',
  '/landing/startup': '/startups',
  '/discover': '/investors',
  '/invitation/investor': '/investors/pricing',
  '/invitation/startup': '/startups/pricing',
  '/signup/investor': '/investors/pricing',
  '/signup/startup': '/startups/pricing',
  '/getfunded': '/startups',
  '/startup': '/startups',
  '/investor-faq': '/investors/faq',
  '/startup-faq': '/startups/faq',
  '/startups-faq': '/startups/faq',
  '/in': '/investors',
  '/login': '/login',
  '/about': '/about-us',
  '/sub1': '/startups/pricing',
  '/sub2': '/investors/pricing',
};

export function normalizeLinkLabel(anchor: HTMLElement): string {
  return anchor.textContent?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';
}

/** Footer/nav labels are authoritative when Builder hrefs are wrong (e.g. About us → /startup). */
export function resolveLabelHref(anchor: HTMLElement): string | undefined {
  const label = normalizeLinkLabel(anchor);
  if (/\babout us\b/.test(label)) return '/about-us';
  if (/\bcontact us\b/.test(label)) return '/contact';
  return undefined;
}

/** Builder nav reuses `/home` for multiple items — disambiguate via visible label. */
export function resolveAmbiguousHref(pathname: string, anchor: HTMLElement): string | undefined {
  if (pathname !== '/home') return undefined;

  const label = normalizeLinkLabel(anchor);
  if (/\babout us\b/.test(label)) return '/about-us';
  if (label.includes('startup')) return '/startups';
  if (label.includes('investor')) return '/investors';
  if (label.includes('log in') || label === 'login') return '/login';
  if (label.includes('faq')) {
    return window.location.pathname.startsWith('/investors') ? '/investors/faq' : '/startups/faq';
  }
  return '/';
}

/** CTA buttons in Builder reuse old invitation URLs — route by button label + current page. */
export function resolveCtaHref(anchor: HTMLElement): string | undefined {
  const label = normalizeLinkLabel(anchor);
  const onStartupPage = window.location.pathname.startsWith('/startups');
  const onInvestorPage = window.location.pathname.startsWith('/investors');

  if (/get discovered by(?: the right)? investors/.test(label)) {
    return '/startups/pricing';
  }

  if (/access the right startups/.test(label)) {
    if (onStartupPage) return '/startups/pricing';
    if (onInvestorPage) return '/investors/pricing';
    return '/startups/pricing';
  }

  return undefined;
}

/** Normalize Builder hrefs (relative or absolute prairi.com) to an in-app route. */
export function resolveBuilderHref(href: string, anchor: HTMLElement): string | null {
  if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) {
    return null;
  }

  let path = href;
  if (href.startsWith('http') || href.startsWith('//')) {
    try {
      const url = new URL(href, window.location.origin);
      const isInternal =
        PRAIRI_LINK_HOSTS.has(url.hostname) || url.hostname === window.location.hostname;
      if (!isInternal) return null;
      path = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  const [pathname, suffix = ''] = path.split(/(?=[?#])/);
  const labelHref = resolveLabelHref(anchor);
  const cta = resolveCtaHref(anchor);
  const ambiguous = resolveAmbiguousHref(pathname, anchor);
  const rewritten = labelHref ?? cta ?? ambiguous ?? BUILDER_LINK_REWRITES[pathname] ?? pathname;
  return rewritten + suffix;
}

/** Intercept Builder <a> clicks (capture) so SPA routes like /startups/pricing open without a refresh. */
export function interceptBuilderLinks(
  root: HTMLElement | null,
  navigate: (to: string) => void
): () => void {
  if (!root) return () => undefined;

  const onClick = (event: MouseEvent) => {
    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    if (!anchor || !root.contains(anchor)) return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    const rewritten = resolveBuilderHref(href, anchor);
    if (!rewritten) return;
    event.preventDefault();
    event.stopPropagation();
    if (shouldRememberInvestorPricingReturn(window.location.pathname, rewritten)) {
      rememberInvestorPricingReturn(anchor);
    }
    navigate(rewritten);
  };

  root.addEventListener('click', onClick, true);
  return () => root.removeEventListener('click', onClick, true);
}

/** Fix About us anchors in rendered Builder HTML (hover URL + native navigation). */
export function patchAboutUsAnchors(root: ParentNode): void {
  root.querySelectorAll('a').forEach(anchor => {
    if (/\babout us\b/i.test(anchor.textContent ?? '')) {
      anchor.setAttribute('href', '/about-us');
    }
  });
}
